import { css, html, LitElement } from "lit";
import { SignalWatcher } from "@lit-labs/signals";
import { customElement, property, state } from "lit/decorators.js";
import { type AppStore } from "../app-store.ts";
import * as Styles from "../../styles.ts"; // Depends where this lives
import "../dui/hstack/dui-hstack.ts";
import "../dui/dialog/dui-dialog.ts";
import "../dui/dialog/dui-dialog-popup.ts";
import "../dui/dialog/dui-dialog-close.ts";
import "../dui/toolbar/dui-toolbar.ts";
import "../dui/button/dui-button.ts";
import "../dui/icon/dui-icon.ts";
import "../dui/menu/dui-menu.ts";
import "../dui/menu/dui-menu-item.ts";
import "../dui/alert-dialog/dui-alert-dialog.ts";
import "../dui/alert-dialog/dui-alert-dialog-popup.ts";
import "../dui/alert-dialog/dui-alert-dialog-title.ts";
import "../dui/alert-dialog/dui-alert-dialog-description.ts";
import "../dui/alert-dialog/dui-alert-dialog-close.ts";
import "./note-edit-form.ts";
import "../app/app-panel.ts";

const styles = css`
  :host {
    display: block;
  }

  .dialog-popup::part(popup) {
    width: calc(var(--space-1) * 180);
    max-width: calc(100vw - var(--space-12));
    padding: 0;
    height: 80vh;
    overflow: hidden;
    container-type: size;
  }

  .dialog-main {
    padding: var(--space-4);
  }
`;

@customElement("note-edit-modal")
export class NoteEditModalElement extends SignalWatcher(LitElement) {
  static override styles = [Styles.legacyBase, styles];

  @property({ attribute: false })
  accessor store: AppStore | undefined = undefined;

  @state()
  accessor #isConfirmDeleteOpen = false;

  #onOpenChange = (event: CustomEvent<{ open: boolean }>): void => {
    this.store?.send({
      type: "toggle-note-edit-modal",
      value: event.detail.open,
    });
  };

  #onClickConfirmDelete = (): void => {
    this.#isConfirmDeleteOpen = true;
  };

  #onDeleteAlertOpenChange = (event: CustomEvent<{ open: boolean }>): void => {
    if (!event.detail.open) {
      this.#isConfirmDeleteOpen = false;
    }
  };

  #onClickDelete = (): void => {
    const state = this.store?.get();
    if (!state?.noteEditSlug || !state?.projectId) return;
    this.store?.send({
      type: "delete-note",
      slug: state.noteEditSlug,
      projectId: state.projectId,
      accessToken: state.session?.access_token,
    });
    this.#isConfirmDeleteOpen = false;
  };

  override render() {
    return html`
      <dui-dialog
        .open="${this.store?.get().isEditNoteModalOpen}"
        @dialog-open-change="${this.#onOpenChange}"
      >
        <dui-dialog-popup class="dialog-popup">
          <app-panel>
            <dui-toolbar
              slot="header"
              size="lg"
              inset
              has-button-left
              has-button-right
            >
              <div slot="center">Edit note</div>
              <dui-dialog-close slot="left">
                <dui-button size="md" variant="ghost" square>
                  <dui-icon icon="close"></dui-icon>
                </dui-button>
              </dui-dialog-close>
              <div slot="right">
                <dui-menu>
                  <dui-button variant="ghost" square slot="trigger">
                    <dui-icon icon="more_horiz"></dui-icon>
                  </dui-button>
                  <dui-menu-item
                    variant="danger"
                    @click="${this.#onClickConfirmDelete}"
                    >Delete</dui-menu-item
                  >
                </dui-menu>
              </div>
            </dui-toolbar>
            <note-edit-form
              class="dialog-main"
              .slug="${this.store?.get().noteEditSlug}"
              .projectId="${this.store?.get().projectId}"
              .accessToken="${this.store?.get().session?.access_token}"
            ></note-edit-form>
          </app-panel>
        </dui-dialog-popup>
      </dui-dialog>

      <dui-alert-dialog
        .open="${this.#isConfirmDeleteOpen}"
        @alert-dialog-open-change="${this.#onDeleteAlertOpenChange}"
      >
        <dui-alert-dialog-popup>
          <dui-alert-dialog-title>Delete note</dui-alert-dialog-title>
          <dui-alert-dialog-description>
            Are you sure you want to delete this&nbsp;note?
          </dui-alert-dialog-description>
          <dui-hstack>
            <dui-alert-dialog-close>
              <dui-button variant="outline">Cancel</dui-button>
            </dui-alert-dialog-close>
            <dui-button variant="destructive" @click="${this.#onClickDelete}">
              Delete
            </dui-button>
          </dui-hstack>
        </dui-alert-dialog-popup>
      </dui-alert-dialog>
    `;
  }
}
